import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import CartDrawer from './CartDrawer';
import AuthModal from './AuthModal';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-300">
              <img 
                src="https://customer-assets.emergentagent.com/job_photo-frames-store-3/artifacts/camza9k4_one2tees%20logo%20plastic%20effect.png" 
                alt="One2Tees USA Logo"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">
                One2Tees USA
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('products')} 
              className="text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
            >
              {t('header.products')}
            </button>
            <button 
              onClick={() => scrollToSection('process')} 
              className="text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
            >
              {t('header.process')}
            </button>
            <button 
              onClick={() => scrollToSection('gallery')} 
              className="text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
            >
              {t('header.gallery')}
            </button>
            <button 
              onClick={() => scrollToSection('testimonials')} 
              className="text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
            >
              {t('header.testimonials')}
            </button>
            <button 
              onClick={() => scrollToSection('faq')} 
              className="text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
            >
              {t('header.faq')}
            </button>
          </nav>

          {/* CTA Button, Auth, Language and Cart */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSelector />
            <AuthModal />
            <CartDrawer buttonOnly />
            <Button 
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              onClick={() => scrollToSection('products')}
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {t('header.startOrder')}
            </Button>
          </div>

          {/* Mobile menu button and cart */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <AuthModal />
            <CartDrawer buttonOnly />
            <button
              className="p-2 text-gray-700 hover:text-cyan-600 transition-colors duration-200"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white border-t border-gray-200">
            <nav className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('products')} 
                className="text-left px-4 py-2 text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg font-medium transition-all duration-200"
              >
                {t('header.products')}
              </button>
              <button 
                onClick={() => scrollToSection('process')} 
                className="text-left px-4 py-2 text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg font-medium transition-all duration-200"
              >
                {t('header.process')}
              </button>
              <button 
                onClick={() => scrollToSection('gallery')} 
                className="text-left px-4 py-2 text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg font-medium transition-all duration-200"
              >
                {t('header.gallery')}
              </button>
              <button 
                onClick={() => scrollToSection('testimonials')} 
                className="text-left px-4 py-2 text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg font-medium transition-all duration-200"
              >
                {t('header.testimonials')}
              </button>
              <button 
                onClick={() => scrollToSection('faq')} 
                className="text-left px-4 py-2 text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg font-medium transition-all duration-200"
              >
                {t('header.faq')}
              </button>
              <div className="px-4 pt-2">
                <Button 
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white font-semibold"
                  onClick={() => scrollToSection('products')}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('header.startOrder')}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
